import { useEffect, useMemo, useRef } from "react"
import { useDaily } from "@daily-co/daily-react"
import { AUTO_SUBSCRIBE_PARTICIPANT_THRESHOLD, MAX_VISIBLE_TILES } from "./constants"

type UseTileSubscriptionCapArgs = {
  participantIds: string[]
  localSessionId: string
  activeSpeakerId: string | null
  screenSharerIds: string[]
  pinnedId?: string | null
  stageId: string | null
}

const dedupe = (ids: Array<string | null | undefined>) => {
  const seen = new Set<string>()
  const result: string[] = []
  ids.forEach((id) => {
    if (!id || seen.has(id)) return
    seen.add(id)
    result.push(id)
  })
  return result
}

/**
 * Keeps large calls usable: below the threshold every participant is rendered and Daily
 * subscribes automatically. Above it, only the most relevant participants get tiles
 * (local, stage, screen sharers, recent speakers, then join order), video is subscribed
 * only for rendered tiles at a thumbnail layer, and audio stays subscribed for everyone.
 */
export function useTileSubscriptionCap({
  participantIds,
  localSessionId,
  activeSpeakerId,
  screenSharerIds,
  stageId,
  pinnedId = null,
}: UseTileSubscriptionCapArgs) {
  const daily = useDaily()
  const recentSpeakersRef = useRef<string[]>([])
  const capped = participantIds.length > AUTO_SUBSCRIBE_PARTICIPANT_THRESHOLD

  useEffect(() => {
    if (!activeSpeakerId) return
    recentSpeakersRef.current = dedupe([activeSpeakerId, ...recentSpeakersRef.current]).slice(0, MAX_VISIBLE_TILES)
  }, [activeSpeakerId])

  const visibleIds = useMemo(() => {
    if (!capped) return participantIds
    const present = new Set(participantIds)
    const ordered = dedupe([
      localSessionId,
      pinnedId,
      stageId,
      activeSpeakerId,
      ...screenSharerIds,
      ...recentSpeakersRef.current,
      ...participantIds,
    ]).filter((id) => present.has(id))
    return ordered.slice(0, MAX_VISIBLE_TILES)
  }, [activeSpeakerId, capped, localSessionId, participantIds, pinnedId, screenSharerIds, stageId])

  useEffect(() => {
    if (!daily) return
    try {
      const current = typeof (daily as any).subscribeToTracksAutomatically === "function"
        ? (daily as any).subscribeToTracksAutomatically()
        : true
      if (current !== !capped) {
        daily.setSubscribeToTracksAutomatically(!capped)
      }
    } catch {
      // Older call objects may not expose the getter; the setter below is best-effort.
    }
  }, [capped, daily])

  useEffect(() => {
    if (!daily || !capped) return
    const visible = new Set(visibleIds)
    const updates: Record<string, any> = {}
    const receiveSettings: Record<string, any> = {}
    participantIds.forEach((id) => {
      if (id === localSessionId) return
      const isVisible = visible.has(id)
      updates[id] = {
        setSubscribedTracks: {
          audio: true,
          video: isVisible,
          screenVideo: isVisible,
          screenAudio: true,
        },
      }
      if (isVisible) {
        receiveSettings[id] = { video: { layer: id === stageId ? 2 : 0 } }
      }
    })
    try {
      if (Object.keys(updates).length) {
        daily.updateParticipants(updates as any)
      }
      if (Object.keys(receiveSettings).length) {
        void daily.updateReceiveSettings(receiveSettings as any)
      }
    } catch {
      // Subscription tuning is an optimisation; the call remains usable without it.
    }
  }, [capped, daily, localSessionId, participantIds, stageId, visibleIds])

  return { visibleIds, capped, hiddenCount: Math.max(0, participantIds.length - visibleIds.length) }
}
